import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import SEO from '../../../../shared/components/SEO/SEO';
import { useAuth } from '../../../../contexts/AuthContext';
import { forumAPI, type ForumCategory, type ForumPost, type ForumAttachment } from '../../../../services/api';
import RichTextEditor from '../../../../shared/components/RichTextEditor/RichTextEditor';
import { stripHtml } from './utils';
import './Community.css';

const MAX_ATTACHMENTS = 1;
const MAX_FILE_SIZE_MB = 5;
const IMAGE_ACCEPT = 'image/jpeg,image/png,image/gif,image/webp';

const EditPost: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { isAuthenticated, user, loading: authLoading } = useAuth();
  const [categories, setCategories] = useState<ForumCategory[]>([]);
  const [post, setPost] = useState<ForumPost | null>(null);
  const [categoryId, setCategoryId] = useState<number | ''>('');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [newFiles, setNewFiles] = useState<File[]>([]);
  const [removedIds, setRemovedIds] = useState<number[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login?next=' + encodeURIComponent('/community/edit/' + id), { replace: true });
      return;
    }
    forumAPI.getCategories()
      .then((res) => setCategories(res.data as ForumCategory[]))
      .catch(() => setCategories([]));
  }, [isAuthenticated, authLoading, navigate, id]);

  useEffect(() => {
    if (!id || !isAuthenticated) return;
    setLoading(true);
    forumAPI.getPost(Number(id))
      .then((res) => {
        const p = res.data as ForumPost;
        setPost(p);
        setCategoryId(p.category.id);
        setTitle(p.title);
        setBody(p.body);
      })
      .catch(() => setPost(null))
      .finally(() => setLoading(false));
  }, [id, isAuthenticated]);

  useEffect(() => {
    if (post && user && post.user.id !== user.id) {
      setPost(null);
    }
  }, [post, user]);

  const existingAttachments = (post?.attachments || []).filter((a) => !removedIds.includes(a.id));
  const totalImages = existingAttachments.length + newFiles.length;
  const canAddMore = totalImages < MAX_ATTACHMENTS;

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const chosen = Array.from(e.target.files || []);
    const maxSize = MAX_FILE_SIZE_MB * 1024 * 1024;
    const valid = chosen.filter((f) => f.size <= maxSize && f.type.startsWith('image/')).slice(0, 1);
    if (valid.length === 0) {
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    if (existingAttachments.length > 0) {
      setRemovedIds((prev) => [...prev, ...existingAttachments.map((a) => a.id)]);
    }
    setNewFiles(valid);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeExisting = (att: ForumAttachment) => {
    setRemovedIds((prev) => [...prev, att.id]);
  };
  const removeNew = (index: number) => {
    setNewFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedBody = body.replace(/<p><br><\/p>/g, '').replace(/<p><\/p>/g, '').trim();
    const hasContent = trimmedBody && stripHtml(trimmedBody).trim();
    if (!post || !categoryId || !title.trim() || !hasContent) {
      setError('Please fill in all required fields: Category, Title, Content, and at least one image.');
      return;
    }
    if (totalImages < 1) {
      setError('At least one image is required. Add or keep an image.');
      return;
    }
    setSubmitting(true);
    setError('');
    const payload = { category: Number(categoryId), title: title.trim(), body: trimmedBody };
    const filesToSend = newFiles.length ? newFiles : undefined;
    Promise.all(removedIds.map((aid) => forumAPI.deletePostAttachment(post.id, aid)))
      .then(() => forumAPI.updatePost(post.id, payload, filesToSend))
      .then(() => {
        navigate('/community/p/' + post.id, { replace: true });
      })
      .catch((err) => {
        const msg = err.response?.data?.title?.[0] || err.response?.data?.body?.[0] || err.response?.data?.detail || 'Failed to update post.';
        setError(typeof msg === 'string' ? msg : 'Failed to update post.');
      })
      .finally(() => setSubmitting(false));
  };

  if (authLoading || !isAuthenticated) {
    return (
      <div className="community-page">
        <div className="community-empty">Loading...</div>
      </div>
    );
  }

  if (loading || !id) {
    return (
      <div className="community-page">
        <div className="community-empty">Loading...</div>
      </div>
    );
  }

  if (!post || (user && post.user.id !== user.id)) {
    return (
      <div className="community-page">
        <div className="community-empty">Post not found or you can’t edit it.</div>
        <Link to="/community" className="community-btn secondary" style={{ marginTop: '1rem' }}>Back to Community</Link>
      </div>
    );
  }

  if (post.status !== 'pending') {
    return (
      <div className="community-page">
        <div className="community-empty">
          This post has been approved or rejected and can no longer be edited.
        </div>
        <Link to={`/community/p/${post.id}`} className="community-btn secondary" style={{ marginTop: '1rem' }}>View post</Link>
        <Link to="/community/new" className="community-btn" style={{ marginTop: '0.5rem', marginLeft: '0.5rem' }}>New post</Link>
      </div>
    );
  }

  return (
    <>
      <SEO title={`Edit: ${post.title} - Crypxa Community`} description="Edit your forum post." />
      <div className="community-page community-form-page">
        <div className="community-header">
          <h1>Edit post</h1>
          <Link to={'/community/p/' + post.id} className="community-btn secondary">Cancel</Link>
        </div>
        <form className="community-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="category">Category <span className="form-required">*</span></label>
            <select
              id="category"
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value ? Number(e.target.value) : '')}
              required
            >
              <option value="">Select category</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="title">Title <span className="form-required">*</span></label>
            <input
              id="title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Post title"
              maxLength={255}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="body">Content <span className="form-required">*</span></label>
            <RichTextEditor
              id="body"
              value={body}
              onChange={setBody}
              placeholder="Write your post..."
            />
          </div>
          <div className="form-group form-group-attachments">
            <label>Images <span className="form-required">*</span></label>
            <p className="form-hint">One image required. Max {MAX_FILE_SIZE_MB}MB. JPEG, PNG, GIF, WebP.</p>
            <input
              ref={fileInputRef}
              type="file"
              accept={IMAGE_ACCEPT}
              multiple
              onChange={onFileChange}
              className="community-file-input-hidden"
              aria-label="Add images"
            />
            <div
              role="button"
              tabIndex={0}
              className="community-attach-zone community-attach-zone-images"
              onClick={() => fileInputRef.current?.click()}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') fileInputRef.current?.click(); }}
              aria-label="Add or replace image"
            >
              <span className="community-attach-icon">🖼</span>
              <span>{totalImages ? 'Image added (click to replace)' : 'Add image'}</span>
            </div>
            {(existingAttachments.length > 0 || newFiles.length > 0) && (
              <div className="community-image-previews">
                {existingAttachments.map((att) => (
                  <div key={att.id} className="community-image-preview-wrap">
                    <img src={att.file_url} alt="" className="community-image-preview" />
                    <button type="button" onClick={() => removeExisting(att)} className="community-attachment-remove community-image-preview-remove" aria-label="Remove" disabled={totalImages <= 1} title={totalImages <= 1 ? 'At least one image is required' : undefined}>×</button>
                  </div>
                ))}
                {newFiles.map((f, i) => (
                  <div key={`new-${i}`} className="community-image-preview-wrap">
                    <img src={URL.createObjectURL(f)} alt="" className="community-image-preview" />
                    <button type="button" onClick={() => removeNew(i)} className="community-attachment-remove community-image-preview-remove" aria-label="Remove" disabled={totalImages <= 1} title={totalImages <= 1 ? 'At least one image is required' : undefined}>×</button>
                  </div>
                ))}
              </div>
            )}
          </div>
          {error && <p style={{ color: 'var(--color-Error)', fontSize: '0.9rem', marginBottom: '0.75rem' }}>{error}</p>}
          <div className="form-actions">
            <button type="submit" disabled={submitting}>
              {submitting ? 'Saving...' : 'Save changes'}
            </button>
            <Link to={'/community/p/' + post.id}>Cancel</Link>
          </div>
        </form>
      </div>
    </>
  );
};

export default EditPost;
